import { billingEventKinds, type BillingEvent, type BillingEventKind } from "@teamna/shared";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Button, DataTable, ErrorState, PageHeader, Select, Toolbar } from "@teamna/ui/components/index.ts";
import { ccKeys, fetchBillingEvents, useAdminBusinesses } from "../lib/cc-api.ts";
import { eventColumns } from "./billing-card.tsx";
import { eventName } from "./billing-format.ts";

/**
 * The billing feed (`/billing`, subscription billing spec, section 6.2): every billing event of every
 * business, newest first, with a filter by kind and by business. The same rows feed the alert
 * emails. "Show older" pages back 50 at a time.
 */
export function BillingFeedScreen() {
  const navigate = useNavigate();
  const businesses = useAdminBusinesses();
  const [kind, setKind] = useState<BillingEventKind | "">("");
  const [businessId, setBusinessId] = useState("");
  const filters = useMemo(() => ({ ...(kind ? { kind } : {}), ...(businessId ? { businessId } : {}) }), [kind, businessId]);

  const feed = useInfiniteQuery({
    queryKey: ccKeys.billingEvents(filters),
    queryFn: ({ pageParam }) => fetchBillingEvents({ ...filters, ...(pageParam ? { before: pageParam } : {}) }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextBefore ?? undefined,
    staleTime: 30 * 1000,
  });
  const rows: BillingEvent[] = useMemo(() => feed.data?.pages.flatMap((page) => page.items) ?? [], [feed.data]);
  const names = useMemo(() => [...(businesses.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)), [businesses.data]);

  return (
    <>
      <PageHeader title="Billing" description="Every billing event, newest first. The alert emails come from the same lines." />
      {feed.error && !feed.data ? (
        <ErrorState error={feed.error} onRetry={() => void feed.refetch()} />
      ) : (
        <>
          <Toolbar>
            <Select label="Filter by event" value={kind} onChange={(event) => setKind(event.target.value as BillingEventKind | "")} className="sm:max-w-[260px]">
              <option value="">All events</option>
              {billingEventKinds.map((value) => (
                <option key={value} value={value}>
                  {eventName(value)}
                </option>
              ))}
            </Select>
            <Select label="Filter by business" value={businessId} onChange={(event) => setBusinessId(event.target.value)} className="sm:max-w-[300px]">
              <option value="">All businesses</option>
              {names.map((business) => (
                <option key={business.id} value={business.id}>
                  {business.name}
                </option>
              ))}
            </Select>
          </Toolbar>
          <DataTable
            columns={eventColumns(undefined, true)}
            rows={rows}
            rowKey={(row) => row.id}
            rowLabel={(row) => `${eventName(row.kind)}, ${row.businessName}`}
            onRowClick={(row) => void navigate({ to: "/businesses/$id", params: { id: row.businessId } })}
            isLoading={feed.isLoading}
            caption="Billing events"
            density="compact"
            countLabel={(count) => `${count} shown`}
            empty={{ title: "No billing events", message: kind || businessId ? "Change the filters." : "Nothing has happened in billing yet." }}
          />
          {feed.hasNextPage ? (
            <div className="mt-4">
              <Button variant="secondary" loading={feed.isFetchingNextPage} onClick={() => void feed.fetchNextPage()}>
                Show older
              </Button>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
